import { LogOut, Moon } from "lucide-react";
import { NavLink, useNavigate } from "react-router";

import { useLogout } from "@/api/hooks";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { Logo } from "@/components/ui/logo";
import { MAIN_NAV, type NavItem, PREFERENCE_NAV } from "@/lib/nav";
import { cn } from "@/lib/utils";

const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "flex h-12 items-center gap-3 rounded-lg px-3 text-sm font-medium text-[#8190b0] transition-colors hover:bg-[#edf2ff] hover:text-[#4077f6] dark:hover:bg-[#1f2630]",
    isActive && "bg-[#4077f6] text-white hover:bg-[#4077f6] hover:text-white",
  );

function NavLinks({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  return (
    <>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink className={linkClass} end={item.end} key={item.to} onClick={onNavigate} to={item.to}>
            <Icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        );
      })}
    </>
  );
}

/** Navigation body shared by the desktop sidebar and the mobile drawer. */
export function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const logout = useLogout();

  async function onLogout() {
    onNavigate?.();
    try {
      await logout.mutateAsync();
    } finally {
      navigate("/login", { replace: true });
    }
  }

  return (
    <div className="flex flex-1 flex-col px-6 py-9">
      <p className="px-3 text-sm uppercase text-[#8190b0]">Menú</p>
      <nav aria-label="Principal" className="mt-4 space-y-3">
        <NavLinks items={MAIN_NAV} onNavigate={onNavigate} />
      </nav>

      <p className="mt-10 px-3 text-sm uppercase text-[#8190b0]">Preferencias</p>
      <div className="mt-4 space-y-3">
        <div className="flex h-12 items-center justify-between rounded-lg px-3 text-sm font-medium text-[#8190b0]">
          <span className="flex items-center gap-3">
            <Moon className="h-5 w-5" />
            Modo oscuro
          </span>
          <ThemeToggle />
        </div>
        <NavLinks items={PREFERENCE_NAV} onNavigate={onNavigate} />
      </div>

      <button
        className="mt-auto flex items-center gap-3 pt-8 text-sm font-medium text-[#ff3434]"
        disabled={logout.isPending}
        onClick={onLogout}
        type="button"
      >
        <LogOut className="h-5 w-5" />
        Cerrar sesión
      </button>
    </div>
  );
}

export function Sidebar() {
  return (
    <aside className="hidden border-r border-[#dfe3eb] bg-white dark:border-[#2e3336] dark:bg-[#101315] xl:sticky xl:top-0 xl:flex xl:h-screen xl:flex-col xl:overflow-y-auto">
      <div className="flex h-[100px] shrink-0 items-center border-b border-[#dfe3eb] px-8 dark:border-[#2e3336]">
        <Logo markClassName="h-12 w-12" textClassName="text-[30px] dark:text-white" />
      </div>
      <SidebarContent />
    </aside>
  );
}
